import { createAsyncThunk,createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import api from '../../api';
import i18n from '../../i18n';

export const getAllAvailableEquipment = createAsyncThunk(
  'equipment/getAllAvailableEquipment',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/equipments');
      // Return all equipment that has instances (availability is date-specific, checked when selecting dates)
      const availableEquipments = res.data.data.filter((item) => item.totalInstances > 0);
      return availableEquipments;
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to fetch all available equipment.';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const getAllEquipment = createAsyncThunk(
  'equipment/getAllEquipment',
  async (equipmentData, { rejectWithValue }) => {
    try {
      console.log('Payload being sent to backend:', equipmentData);
      const res = await api.get('/equipments');
      return res.data.data;
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to fetch all equipment.';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const getSingleEquipment = createAsyncThunk(
  'equipment/getSingleEquipment',
  async (equipmentId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/equipments/${equipmentId}`);
      return res.data.data.equipment;
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to get that equipment.';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const getEquipmentContact = createAsyncThunk(
  'equipment/getEquipmentContact',
  async (equipmentId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/equipments/${equipmentId}/contact`);
      return res.data.data.ownerInfo;
    } catch (error) {
      let errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.errors?.[0]?.message ||
        error.message ||
        'An error occurred while trying to update your password.';
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteEquipment = createAsyncThunk(
  'equipment/deleteEquipment',
  async (equipmentId, { rejectWithValue }) => {
    try {
      await api.delete(`/equipments/${equipmentId}`);
      return equipmentId;
    } catch (error) {
      toast.warn(error.errors.message);
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to delete that equipment.';
      return rejectWithValue(errorMessage);
    }
  }
);

export const addEquipment = createAsyncThunk(
  'equipment/addEquipment',
  async (equipmentData, { rejectWithValue }) => {
    try {
      const res = await api.post('/equipments', equipmentData);
      return res.data.data;
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to add that equipment.';
      return rejectWithValue(errorMessage);
    }
  }
);
export const updateEquipment = createAsyncThunk(
  'equipments/updateEquipment',
  async (equipmentData, { rejectWithValue }) => {
    try {
      const response = await api.put(`/equipments/${equipmentData.id}`, equipmentData);
      if (!response) {
        return Error('bad');
      }
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
// Async Thunk to fetch reservations for equipment
export const getReservationsForEquipment = createAsyncThunk(
  'equipment/getReservationsForEquipment',
  async (equipmentTypeId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/reservations?equipmentTypeId=${equipmentTypeId}`);
      return res.data.data.reservations;
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to fetch reservations.';
      return rejectWithValue(errorMessage);
    }
  }
);

// Async Thunk for filtering equipment with date range (includes reservation checking)
export const filterEquipmentWithDateRange = createAsyncThunk(
  'equipment/filterEquipmentWithDateRange',
  async (
    { priceRange, locationOptions, equipmentTypeOptions, priceSort, dateRange },
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const equipments = state.equipments.equipments;

      // If no date range is selected, use the regular filter
      if (!dateRange || !dateRange.from) {
        return {
          filteredEquipments: equipments.filter((item) => {
            const isLocationMatch =
              !locationOptions ||
              Object.keys(locationOptions).length === 0 ||
              locationOptions?.[item.location];
            const isTypeMatch =
              !equipmentTypeOptions ||
              Object.keys(equipmentTypeOptions).length === 0 ||
              equipmentTypeOptions?.[item.category];
            const isPriceMatch =
              !priceRange ||
              (priceRange.minValue <= item.rentalPrice && item.rentalPrice <= priceRange.maxValue);
            return isLocationMatch && isTypeMatch && isPriceMatch;
          }),
          priceSort,
        };
      }

      // Helper function to check date conflicts
      const hasDateConflict = (reservations, selectedStart, selectedEnd) => {
        return reservations.some((reservation) => {
          if (
            !reservation.startDate ||
            !reservation.endDate ||
            reservation.reservationStatus?.toLowerCase() === 'canceled'
          ) {
            return false;
          }

          const resStart = new Date(reservation.startDate);
          const resEnd = new Date(reservation.endDate);

          // Check for overlap
          return selectedStart <= resEnd && selectedEnd >= resStart;
        });
      };

      // Filter equipment and check availability
      const filteredEquipments = [];

      for (const equipment of equipments) {
        // Apply basic filters first
        const isLocationMatch =
          !locationOptions ||
          Object.keys(locationOptions).length === 0 ||
          locationOptions?.[equipment.location];
        const isTypeMatch =
          !equipmentTypeOptions ||
          Object.keys(equipmentTypeOptions).length === 0 ||
          equipmentTypeOptions?.[equipment.type];
        const isPriceMatch =
          !priceRange ||
          (priceRange.minValue <= equipment.rentalPrice &&
            equipment.rentalPrice <= priceRange.maxValue);

        if (!isLocationMatch || !isTypeMatch || !isPriceMatch) {
          continue;
        }

        // Check equipment availability (has at least 1 available instance)
        if (!equipment.availableInstances || equipment.availableInstances <= 0) {
          continue;
        }

        // Fetch reservations for this equipment
        try {
          const res = await api.get(`/reservations?equipmentTypeId=${equipment._id}`);
          const reservations = res.data.data.reservations || [];

          // Check for date conflicts
          const selectedStart = new Date(dateRange.from);
          const selectedEnd = dateRange.to ? new Date(dateRange.to) : selectedStart;

          if (!hasDateConflict(reservations, selectedStart, selectedEnd)) {
            filteredEquipments.push(equipment);
          }
        } catch (error) {
          // If we can't fetch reservations, include the equipment (assume available)
          console.warn(`Could not fetch reservations for equipment ${equipment._id}:`, error);
          filteredEquipments.push(equipment);
        }
      }

      return {
        filteredEquipments,
        priceSort,
      };
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong while filtering equipment.';
      return rejectWithValue(errorMessage);
    }
  }
);

// New Async Thunk for Renting Equipment
export const rentEquipment = createAsyncThunk(
  'equipment/rentEquipment',
  async ({ id, quantity }, { rejectWithValue }) => {
    try {
      // Validate inputs
      if (!id) {
        throw new Error('Equipment ID is required');
      }
      if (!quantity || quantity <= 0) {
        throw new Error('Valid quantity is required');
      }

      console.log('Making rental request with:', { id, quantity });

      const res = await api.post(`/equipments/rent/${id}`, {
        quantity: parseInt(quantity),
      });

      if (res.data && res.data.success) {
        return {
          id,
          quantity: parseInt(quantity),
          equipment: res.data.equipment,
        };
      } else {
        throw new Error(res.data?.message || 'Failed to rent equipment');
      }
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to rent that equipment.';
      return rejectWithValue(errorMessage);
    }
  }
);

const initialState = {
  equipments: [],
  filteredEquipments: [], // Add filteredEquipments to the state
  loading: false,
  totalPages: null,
  selectedEquipment: null,
  error: null,
  cart: [],
  rentLoading: false,
  ownerContact: null,
  reservations: {}, // Store reservations by equipment ID
};

const equipmentSlice = createSlice({
  name: 'equipment',
  initialState,

  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    filterEquipment: (state, action) => {
      const { price, location, equipmentType, date, onlyAvailable, searchTerm } = action.payload;
      const source = onlyAvailable ? state.availableEquipments : state.equipments;

      state.filteredEquipments = source.filter((item) => {
        const isLocationMatch = location ? item.location === location : true;
        const isTypeMatch = equipmentType ? item.category === equipmentType : true;
        const isDateMatch = date ? new Date(item.availableDate) >= new Date(date) : true;
        const isSearchMatch = searchTerm
          ? item.name.toLowerCase().includes(searchTerm.toLowerCase())
          : true;
        return isLocationMatch && isTypeMatch && isDateMatch && isSearchMatch;
      });

      if (price) {
        state.filteredEquipments = state.filteredEquipments.sort((a, b) => {
          if (price === 'asc') {
            return a.rentalPrice - b.rentalPrice;
          } else if (price === 'desc') {
            return b.rentalPrice - a.rentalPrice;
          }
          return 0;
        });
      }
    },
    filterEquipmentWithRanges: (state, action) => {
      // Helper function to check equipment availability during date range
      const checkEquipmentAvailability = (equipment, dateRange) => {
        // Check if equipment has available instances
        if (!equipment.availableInstances || equipment.availableInstances <= 0) {
          return false;
        }

        // For now, we'll implement a basic check
        // In a real implementation, you would need to:
        // 1. Fetch reservations for this specific equipment
        // 2. Check if the selected date range conflicts with existing reservations
        // 3. Return false if there are conflicts

        // Since we don't have access to reservation data in this slice,
        // we'll need to either:
        // - Pass reservation data as a parameter
        // - Fetch it from the backend
        // - Move this logic to a component that has access to reservation data

        console.log(
          'Checking availability for equipment:',
          equipment.name,
          'with date range:',
          dateRange
        );

        // TODO: Implement real reservation conflict checking
        // This is a placeholder - it should check against actual reservations
        return true;
      };

      // TODO: this will need to be reconciled with the above filterEquipment
      const { priceRange, locationOptions, equipmentTypeOptions, priceSort, dateRange } =
        action.payload;

      const source = state.equipments;
      state.filteredEquipments = source.filter((item) => {
        const isLocationMatch =
          !locationOptions ||
          Object.keys(locationOptions).length === 0 ||
          locationOptions?.[item.location];
        const isTypeMatch =
          !equipmentTypeOptions ||
          Object.keys(equipmentTypeOptions).length === 0 ||
          equipmentTypeOptions?.[item.type];
        const isPriceMatch =
          !priceRange ||
          (priceRange.minValue <= item.rentalPrice && item.rentalPrice <= priceRange.maxValue);

        // Add date range filtering logic
        const isDateMatch =
          !dateRange || !dateRange.from || checkEquipmentAvailability(item, dateRange);

        return isLocationMatch && isTypeMatch && isPriceMatch && isDateMatch;
      });

      if (priceSort && Object.keys(priceSort).length > 0) {
        state.filteredEquipments = state.filteredEquipments.sort((a, b) => {
          if (priceSort['Low to High']) {
            return a.rentalPrice - b.rentalPrice;
          } else if (priceSort['High to Low']) {
            return b.rentalPrice - a.rentalPrice;
          }
          return 0;
        });
      }
    },
    resetFilteredEquipments: (state) => {
      state.filteredEquipments = state.equipments; // Reset to all equipment
    },

    removeFromCart: (state, action) => {
      const { id } = action.payload;
      state.cart = state.cart.filter((item) => item.id !== id);
    },

    updateCartQuantity: (state, action) => {
      const { id, quantity } = action.payload;
      const itemIndex = state.cart.findIndex((item) => item.id === id);
      if (itemIndex >= 0) {
        state.cart[itemIndex].quantity = quantity;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllAvailableEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllAvailableEquipment.fulfilled, (state, action) => {
        state.loading = false;
        state.equipments = action.payload;
        if (state.filteredEquipments.length === 0) {
          state.filteredEquipments = action.payload; // Default filter if empty
        }
      })
      .addCase(getAllAvailableEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getAllEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllEquipment.fulfilled, (state, action) => {
        state.loading = false;
        state.equipments = action.payload;
        state.filteredEquipments = action.payload;
      })
      .addCase(getAllEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getSingleEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.ownerContact = null;
      })
      .addCase(getSingleEquipment.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedEquipment = action.payload;
      })
      .addCase(getSingleEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getEquipmentContact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEquipmentContact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getEquipmentContact.fulfilled, (state, action) => {
        state.loading = false;
        state.ownerContact = action.payload;
      })
      .addCase(deleteEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEquipment.fulfilled, (state, action) => {
        state.loading = false;
        state.equipments = state.equipments.filter((item) => item._id !== action.payload);
        state.filteredEquipments = state.filteredEquipments.filter(
          (item) => item._id !== action.payload
        );
      })
      .addCase(deleteEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addEquipment.fulfilled, (state, action) => {
        state.equipments.push(action.payload);
        state.loading = false;
        state.filteredEquipments.push(action.payload);
      })
      .addCase(addEquipment.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(addEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEquipment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEquipment.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.equipments.findIndex(
          (item) => item._id === action.payload.equipment._id
        );
        if (index !== -1) {
          state.equipments[index] = action.payload.equipment;
        }
      })

      .addCase(updateEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Rent Equipment (Async Thunk)
      .addCase(rentEquipment.fulfilled, (state, action) => {
        state.rentLoading = false;
        const { id, quantity, equipment } = action.payload;

        // Update the cart
        const existingCartItem = state.cart.find((item) => item.id === id);
        if (existingCartItem) {
          existingCartItem.quantity += quantity;
        } else {
          state.cart.push({
            id,
            quantity,
            price: equipment?.rentalPrice || 0,
          });
        }

        // Update equipment in the list
        const equipmentIndex = state.equipments.findIndex((item) => item.id === id);
        if (equipmentIndex >= 0) {
          state.equipments[equipmentIndex].quantity -= quantity;
          // Update filtered equipments as well if they exist
          const filteredIndex = state.filteredEquipments.findIndex((item) => item.id === id);
          if (filteredIndex >= 0) {
            state.filteredEquipments[filteredIndex].quantity -= quantity;
          }
        }

        toast.success(i18n.t('equipment.successfullyRented', { quantity }));
      })
      // Filter Equipment with Date Range
      .addCase(filterEquipmentWithDateRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(filterEquipmentWithDateRange.fulfilled, (state, action) => {
        state.loading = false;
        state.filteredEquipments = action.payload.filteredEquipments;

        // Apply sorting if specified
        if (action.payload.priceSort && Object.keys(action.payload.priceSort).length > 0) {
          state.filteredEquipments = state.filteredEquipments.sort((a, b) => {
            if (action.payload.priceSort['Low to High']) {
              return a.rentalPrice - b.rentalPrice;
            } else if (action.payload.priceSort['High to Low']) {
              return b.rentalPrice - a.rentalPrice;
            }
            return 0;
          });
        }
      })
      .addCase(filterEquipmentWithDateRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { filterEquipment, filterEquipmentWithRanges, clearError } = equipmentSlice.actions;

export default equipmentSlice.reducer;
